<template>
  <v-dialog
    v-model="isDialogOpen"
    max-width="700"
    :persistent="!disabled"
    @click:outside="close"
    @afterLeave="close"
    :fullscreen="mobile"
  >
    <v-card data-testid="content-creator-edit-text-dialog">
      <v-card-title class="pa-0">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>
            {{ $t('content-creator.edit-text') }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <!-- TODO: check unsupported prop: dark -->
            <v-btn
              icon
              @click="close"
            >
              <v-icon size="large"> mdi-close </v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>

      <v-card-text class="pb-0 mt-2">
        <BlurredBackdropUpselling
          :is-blurred="disabled"
          margin-top="100px"
          :class="{ 'd-flex flex-column gap-1': true, 'h-full': mobile }"
        >
          <h5 class="text-h6">
            {{ this.$t('content-creator.customize-text') }}
          </h5>

          <v-textarea
            v-if="kind === kindType.newsletter"
            v-model="subject"
            :label="$t('content-creator.textarea.edit-content.subject')"
            variant="outlined"
            rows="1"
            :error="subjectError"
            :error-messages="
              subjectError ? $t('alerts.content-creator.edit-subject-label.error') : ''
            "
          />
          <div
            v-if="kind !== kindType.newsletter && isMasterView"
            class="d-flex flex-wrap gap-1 mb-2"
          >
            <v-tooltip
              v-for="placeholder in placeholders"
              :key="placeholder.value"
              :disabled="!placeholder.tooltip"
              location="top"
            >
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <v-chip
                    rounded="pill"
                    color="primary"
                    :disabled="disabled || placeholder.disabled"
                    @click="insertPlaceholder(placeholder.value)"
                  >
                    {{ placeholder.label }}
                  </v-chip>
                </span>
              </template>
              <span>{{ placeholder.tooltip }}</span>
            </v-tooltip>
          </div>
          <v-textarea
            ref="mainTextarea"
            :label="
              kind === kindType.newsletter
                ? $t('content-creator.textarea.edit-content.text.one')
                : $t('content-creator.textarea.edit-content.text.two')
            "
            v-model="text"
            variant="outlined"
            :disabled="disabled"
            rows="8"
            :error="textError"
            :error-messages="textError ? $t('alerts.content-creator.edit-text.error') : ''"
            data-testid="content-creator-edit-text-textarea"
            @mouseup="saveCursorPosition"
            @keyup="saveCursorPosition"
            @blur="saveCursorPosition"
          />
          <v-textarea
            v-if="kind === kindType.newsletter && topic"
            v-model="buttonLabel"
            :label="$t('content-creator.textarea.edit-content.button')"
            variant="outlined"
            rows="1"
            :error="buttonLabelError"
            :error-messages="
              buttonLabelError ? $t('alerts.content-creator.edit-button-label.error') : ''
            "
          />

          <v-alert
            v-for="limit in textLimits"
            :key="limit.translationKey"
            type="error"
            :text="$t('content-creator.limits.' + limit.translationKey, limit.params)"
            variant="tonal"
            density="compact"
          />
        </BlurredBackdropUpselling>
      </v-card-text>
      <v-divider class="mt-4" />
      <v-spacer v-if="!mobile" />
      <v-card-actions
        :class="{ 'd-flex justify-end': true, 'edit-content__actions--margin': mobile }"
      >
        <v-btn
          variant="text"
          data-testid="content-creator-edit-text-close-btn"
          @click="close"
        >
          {{ $t('buttons.content-creator.close') }}
        </v-btn>
        <v-btn
          variant="text"
          :disabled="disabled"
          color="primary"
          data-testid="content-creator-edit-text-apply-btn"
          @click="update"
        >
          {{ $t('buttons.content-creator.apply') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import branding from '@/mixins/branding'
import feature from '@/mixins/feature'
import KindType from '@/modules/contentCreator/creator/enums/KindType.js'
import {
  PLACEHOLDER_LOCATION,
  PLACEHOLDER_OFFICE_NAME,
} from '@/modules/contentCreator/creator/lib/placeholders.js'

export default {
  components: {
    BlurredBackdropUpselling,
  },
  mixins: [feature, branding],
  props: {
    content: {
      type: Object,
      default: () => ({}),
    },
    kind: {
      type: String,
      default: '',
    },
    topic: {
      type: Boolean,
      default: true,
    },
    forceEditable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      disabled: false,
      isDialogOpen: true,
      text: this.content.text,
      buttonLabel: '',
      subject: '',
      subjectError: false,
      buttonLabelError: false,
      textError: false,
      cursorPosition: null,
    }
  },
  mounted() {
    if (this.forceEditable) return
    const feature = this.getFeature(this.featureNames.CONTENT_CREATOR)
    this.disabled = !feature.config.canEditPost

    if (this.kind === KindType.newsletter) {
      this.divideTextIntoSections()
    }
  },

  computed: {
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    wasTextEdited() {
      return this.text !== this.content.text
    },
    kindType() {
      return KindType
    },
    mobile() {
      return this.$vuetify.display.smAndDown
    },
    placeholders() {
      const isDisabled = (placeholder) => this.text.includes(placeholder)

      return [
        {
          value: PLACEHOLDER_OFFICE_NAME,
          label: this.$t('content-creator.placeholders.office-name'),
          disabled: isDisabled(PLACEHOLDER_OFFICE_NAME),
          tooltip: isDisabled(PLACEHOLDER_OFFICE_NAME)
            ? this.$t('content-creator.placeholders.office-name-tooltip')
            : '',
        },
        {
          value: PLACEHOLDER_LOCATION,
          label: this.$t('content-creator.placeholders.location'),
          disabled: isDisabled(PLACEHOLDER_LOCATION),
          tooltip: isDisabled(PLACEHOLDER_LOCATION)
            ? this.$t('content-creator.placeholders.location-tooltip')
            : '',
        },
      ]
    },
    textLimits() {
      if (!this.text) return []

      const hashtags = this.text.match(/#[^\s#]+/g) || []
      const length = this.text.length

      return [
        {
          exceeded: length > 2200,
          translationKey: 'instagram-caption',
          kind: KindType.instagramPost,
          params: { current: length, limit: 2200 },
        },
        {
          exceeded: hashtags.length > 30,
          translationKey: 'instagram-hashtags',
          kind: KindType.instagramPost,
          params: { current: hashtags.length, limit: 30 },
        },
        {
          exceeded: length > 3000,
          translationKey: 'linkedin-post',
          kind: KindType.linkedinPost,
          params: { current: length, limit: 3000 },
        },
        {
          exceeded: length > 63206,
          translationKey: 'facebook-post',
          kind: KindType.facebookPost,
          params: { current: length, limit: 63206 },
        },
      ].filter((limit) => limit.exceeded && this.kind === limit.kind)
    },
  },

  methods: {
    saveCursorPosition(event) {
      this.cursorPosition = event.target.selectionStart
    },
    getTextareaElement() {
      return this.$refs.mainTextarea?.$el?.querySelector('textarea')
    },
    insertPlaceholder(placeholder) {
      if (this.disabled) return

      const textarea = this.getTextareaElement()
      const position = this.cursorPosition ?? textarea?.selectionStart ?? this.text?.length ?? 0
      const before = this.text.slice(0, position)
      const after = this.text.slice(position)

      this.text = `${before}${placeholder}${after}`
    },
    divideTextIntoSections() {
      const subjectMatch = this.text.match(/^(Subject:|Betreff:)\s*(.*)/i)
      if (subjectMatch) {
        this.subject = subjectMatch[2]

        const buttonMatch = this.text.match(/<CtaButton[^>]*>([\s\S]*?)<\/CtaButton>/i)
        this.buttonLabel = buttonMatch ? buttonMatch[1].trim() : ''

        this.text = this.text
          .replace(/^(Subject:|Betreff:)\s*(.*)\n*/i, '')
          .replace(/<\/?Paragraph>/gi, '')
          .replace(/[\n\r\s]*<CtaButton\b[^>]*>[\s\S]*?<\/CtaButton>[\n\r\s]*/gi, '')
          .trim()
      }
    },
    prepareNewsletterText() {
      const textWithoutSubject = this.text.trim()
      let paragraphs = textWithoutSubject.split(/\n\n+/)
      const buttonHtml = `<CtaButton style="
          background-color: ${this.primaryColor};
          color: #ffffff;
          padding: 10px;
          width: 100px;
          border: none;
          text-shadow: none;
          font-weight: bold;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        " :link="subscriptionLink">${this.buttonLabel}</CtaButton>`

      if (paragraphs.length > 1) {
        const lastParagraph = paragraphs.pop()
        if (this.topic) {
          paragraphs.push(buttonHtml)
        }
        paragraphs.push(lastParagraph)
      } else if (this.topic) {
        paragraphs.push(buttonHtml)
      }

      paragraphs = paragraphs.map((p) =>
        p.includes('<CtaButton') ? p : `<Paragraph>${p}</Paragraph>`
      )

      // Rebuild text with Subject: at top
      return `Subject: ${this.subject}\n\n${paragraphs.join('\n\n')}`
    },

    update() {
      this.resetErrors()
      this.validateFields()

      if (this.subjectError || this.buttonLabelError || this.textError) {
        return
      }

      if (this.wasTextEdited) this.$tracking.event('Content Creator', 'Edited', 'Content Text')
      this.$emit(
        'update',
        this.kind === KindType.newsletter ? this.prepareNewsletterText() : this.text
      )
      this.close()
    },
    validateFields() {
      if (this.kind === KindType.newsletter) {
        if (!this.subject || this.subject.length === 0) {
          this.subjectError = true
        }
        if (this.topic && (!this.buttonLabel || this.buttonLabel.length === 0)) {
          this.buttonLabelError = true
        }
      }
      if (!this.text || this.text.length === 0) {
        this.textError = true
      }
    },
    resetErrors() {
      this.subjectError = false
      this.buttonLabelError = false
      this.textError = false
    },
    close() {
      this.isDialogOpen = false
      this.$emit('close')
    },
  },
  emits: ['update', 'close'],
}
</script>
<style scoped>
.edit-content__actions--margin {
  margin-bottom: 80px;
}
</style>
